import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/ui/Toaster'
import { adminGet, adminKeys, adminPost } from '../shared/api'
import type { AgentPhotosInput, AgentPhotosStart, AgentSearchStart, AgentSearchState, AgentSimilarInput, AiSearchInput, FoundSearchInput, ProspectPhotos, AiSearchResult, Prospect, ProspectAction, ProspectKind, ProspectList } from './contract'

/* Rede — leitura e ações do motor de prospecção (GET/POST /admin/prospects/:kind…). Toda ação devolve o prospect
   atualizado, que já entra na lista em cache (sem esperar o refetch). */

export const prospectKeys = {
    list: (kind: ProspectKind) => ['panel', 'admin', 'prospects', kind] as const,
    photos: (kind: ProspectKind) => ['panel', 'admin', 'prospects', kind, 'photos'] as const,
}

/** Fotos das sugestões (as que o agente indicou e o servidor baixou). */
export const prospectPhotosQuery = (kind: ProspectKind) =>
    queryOptions({ queryKey: prospectKeys.photos(kind), queryFn: () => adminGet<ProspectPhotos>(`/prospects/${kind}/photos`), staleTime: 60_000 })

export const prospectsQuery = (kind: ProspectKind) =>
    queryOptions({ queryKey: prospectKeys.list(kind), queryFn: () => adminGet<ProspectList>(`/prospects/${kind}`), refetchInterval: 60_000 })

function replaceIn(list: ProspectList | undefined, p: Prospect): ProspectList | undefined {
    if (!list) return list
    const found = list.items.some(x => x.id === p.id)
    return { ...list, items: found ? list.items.map(x => (x.id === p.id ? p : x)) : [...list.items, p] }
}

/**
 * POST /admin/prospects/:kind/<action>. `message` = toast de sucesso (texto ou função do prospect devolvido);
 * `undo` = "Desfazer" no toast (recebe o corpo e o resultado).
 */
export function useProspectAction<TBody extends { id: string }>(
    kind: ProspectKind,
    action: ProspectAction,
    options: { message?: string | ((p: Prospect, body: TBody) => string); undo?: (body: TBody, p: Prospect) => void } = {},
) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: TBody) => adminPost<Prospect>(`/prospects/${kind}/${action}`, body),
        onSuccess: (p, body) => {
            client.setQueryData<ProspectList>(prospectKeys.list(kind), old => replaceIn(old, p))
            void client.invalidateQueries({ queryKey: adminKeys.today })
            const message = typeof options.message === 'function' ? options.message(p, body) : options.message
            if (message) toast({ message, action: options.undo ? { label: 'Desfazer', onClick: () => options.undo?.(body, p) } : undefined })
        },
    })
}

export function useClientReset() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: () => adminPost<{ ok: true; message: string }>('/prospects/ambassador/client-reset'),
        onSuccess: r => {
            void client.invalidateQueries({ queryKey: prospectKeys.list('ambassador') })
            toast({ message: r.message })
        },
    })
}

/** Busca com IA (perfil/nicho): as novas sugestões entram na lista; devolve os ids desta busca. */
export function useAiSearch(kind: ProspectKind) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: AiSearchInput) => (kind === 'ambassador' || kind === 'corporate' ? agentSearch(kind, body) : directSearch(kind, body)),
        onSuccess: r => {
            client.setQueryData<ProspectList>(prospectKeys.list(kind), old => (old ? { ...old, items: [...old.items.filter(x => !r.ids.includes(x.id)), ...r.items] } : old))
            void client.invalidateQueries({ queryKey: prospectKeys.photos(kind) })
            // As telas "Já buscados" (base 009) ganham o que a busca achou
            void client.invalidateQueries({ queryKey: FOUND_KEY })
        },
    })
}

/** "Buscar na base de dados": o que as buscas já guardaram (base 009) e combina com a busca, sem chamar o agente. */
export function useFoundSearch(kind: ProspectKind) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: FoundSearchInput) => adminPost<AiSearchResult>(`/prospects/${kind}/found-search`, body),
        onSuccess: r => client.setQueryData<ProspectList>(prospectKeys.list(kind), old => (old ? { ...old, items: [...old.items.filter(x => !r.ids.includes(x.id)), ...r.items] } : old)),
    })
}

const directSearch = (kind: ProspectKind, body: AiSearchInput) => adminPost<AiSearchResult>(`/prospects/${kind}/ai-search`, body)

/** Prefixo das listas "Já buscados" (rede/ui/found.tsx). */
export const FOUND_KEY = ['panel', 'admin', 'rede-found'] as const

/** De quanto em quanto tempo a tela pergunta se o agente já respondeu, e até quando espera. */
export const AGENT_POLL_MS = 4_000
export const AGENT_WAIT_MS = 8 * 60_000

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Busca pelo agente "Busca de Embaixadores" do Cognitive: o servidor abre a tarefa no motor e devolve um ticket; a tela
 * consulta até o agente responder (as sugestões já chegam gravadas na Rede). Sem agente, o servidor responde `direct` e
 * vale a busca direta com IA.
 */
export async function agentSearch(kind: ProspectKind, body: AiSearchInput, wait: AgentWait = {}): Promise<AgentSearchResult> {
    const start = await adminPost<AgentSearchStart>(`/prospects/${kind}/agent-search`, body)
    if (start.mode === 'direct') return directSearch(kind, body)
    if (start.mode === 'cached') return { ids: start.ids, items: start.items, cachedAt: start.at }
    return waitForAgent(kind, { ticket: start.ticket, q: body.q, filters: body.filters ?? {} }, wait)
}

/** Resultado de uma busca na tela: `cachedAt` = a busca já tinha sido feita e veio do que está guardado (base 009). */
export type AgentSearchResult = AiSearchResult & { cachedAt?: string }

/** Sem agente, a busca por semelhança não tem para onde cair: a tela avisa e mostra o que já está na Rede. */
export const AGENT_UNAVAILABLE = 'agent_unavailable'

/** Semelhantes aos atuais pelo agente: os embaixadores-base escolhidos vão junto; cada sugestão volta ligada a um deles. */
export async function agentSimilar(kind: ProspectKind, body: AgentSimilarInput, wait: AgentWait = {}): Promise<AgentSearchResult> {
    const start = await adminPost<AgentSearchStart>(`/prospects/${kind}/agent-similar`, body)
    if (start.mode === 'direct') throw new Error(AGENT_UNAVAILABLE)
    if (start.mode === 'cached') return { ids: start.ids, items: start.items, cachedAt: start.at }
    return waitForAgent(kind, { ticket: start.ticket, bases: body.bases.map(b => b.id), filters: body.filters ?? {} }, wait)
}

type AgentWait = { pollMs?: number; waitMs?: number }

/** Consulta o resultado até o agente responder (as sugestões já chegam gravadas na Rede). */
async function waitForAgent(kind: ProspectKind, body: { ticket: string; q?: string; bases?: string[]; photoIds?: string[]; filters?: Record<string, string> }, { pollMs = AGENT_POLL_MS, waitMs = AGENT_WAIT_MS }: AgentWait): Promise<AiSearchResult> {
    const deadline = Date.now() + waitMs
    while (Date.now() < deadline) {
        await sleep(pollMs)
        const s = await adminPost<AgentSearchState>(`/prospects/${kind}/agent-search/result`, body)
        if (s.state === 'done') return { ids: s.ids, items: s.items }
        if (s.state === 'failed' || s.state === 'gone') throw new Error(`agent_search_${s.state}`)
    }
    throw new Error('agent_search_timeout')
}

/** Semelhantes aos atuais (agente): as novas sugestões entram na lista, como na busca por perfil. */
export function useSimilarSearch(kind: ProspectKind) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: AgentSimilarInput) => agentSimilar(kind, body),
        onSuccess: r => {
            client.setQueryData<ProspectList>(prospectKeys.list(kind), old => (old ? { ...old, items: [...old.items.filter(x => !r.ids.includes(x.id)), ...r.items] } : old))
            void client.invalidateQueries({ queryKey: prospectKeys.photos(kind) })
            // As telas "Já buscados" (base 009) ganham o que a busca achou
            void client.invalidateQueries({ queryKey: FOUND_KEY })
        },
    })
}

/** Resultado de "Buscar fotos": ids com foto do Instagram, ids com foto do agente e quantos ficaram sem o agente. */
export type PhotosFound = { instagram: string[]; agent: string[]; unavailable: number }

/** Fotos dos embaixadores atuais sem foto: o Instagram (pelo Hub) primeiro; o agente procura os que sobraram. */
export async function agentPhotos(kind: ProspectKind, body: AgentPhotosInput, wait: AgentWait = {}): Promise<PhotosFound> {
    const start = await adminPost<AgentPhotosStart>(`/prospects/${kind}/agent-photos`, body)
    const instagram = start.instagram ?? []
    if (start.mode !== 'agent') {
        // Nada pelo Instagram e o agente fora: o mesmo aviso de antes
        if (!instagram.length && start.pending > 0) throw new Error(AGENT_UNAVAILABLE)
        return { instagram, agent: [], unavailable: start.pending }
    }
    const photoIds = body.people.map(p => p.id).filter(id => !instagram.includes(id))
    return { instagram, agent: (await waitForAgent(kind, { ticket: start.ticket, photoIds }, wait)).ids, unavailable: 0 }
}

const fotos = (n: number) => `${n} ${n === 1 ? 'foto' : 'fotos'}`

/** A frase depois de "Buscar fotos": de onde veio cada foto e quantos ficaram sem o agente. */
export function photosNote({ instagram, agent, unavailable }: PhotosFound): string {
    const parts = [instagram.length && `${fotos(instagram.length)} do Instagram`, agent.length && `${fotos(agent.length)} pelo agente`].filter(Boolean)
    const found = parts.length ? `${parts.join(' e ')}.` : unavailable ? '' : 'Nenhuma foto confiável encontrada.'
    const rest = unavailable ? ` O agente do Cognitive não está disponível para ${unavailable === 1 ? 'o outro' : `os outros ${unavailable}`}.` : ''
    return `${found}${rest}`.trim()
}

/** "Buscar fotos": as fotos novas aparecem nas telas dos embaixadores atuais. */
export function useAgentPhotos(kind: ProspectKind) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: AgentPhotosInput) => agentPhotos(kind, body),
        onSuccess: () => void client.invalidateQueries({ queryKey: adminKeys.area('embaixadores', 'photos') }),
    })
}

